import { withStyles, Grid, Typography, Paper, Tooltip, Button } from '@material-ui/core';
import React from 'react'; // eslint-disable-line
import styles, { StyledComponent } from '../../../../../styles/styles';
import { LabeledValue } from '../../../../../controls/labeled-value';
import { Edit } from '@material-ui/icons';
import { connect } from 'react-redux';
import { State } from '../../../../../redux/root-reducer';
import { FormDetails, getFormDetails } from '../form-details-reducer';
import { getDisplayDate } from '../../../../../shared-functions';
import { OpenModal, openModalAction } from '../../../../../form-modal/actions';
import { editLifeAssureDetails } from './life-assured-edit';
import { editPolicyHolderDetails } from './policy-holder-edit';
import { UpdateFormDetails, updateFormDetailsAction } from '../actions/update-form-details';
import { getClaim } from '../../../../../claim';
import { Nullable } from '../../../../../shared-types';
import { ClaimType } from '../../../../../claim/claim-types';
import { AllRelationships } from '../../../../../codes/relationship-types';

interface BaseProps {
    formDetails?: FormDetails;
    disableEditButton?: boolean;
    updateFormDetails?: UpdateFormDetails;
    type: string;
    claimType?: Nullable<ClaimType>;
}

interface Props extends BaseProps, StyledComponent {
    readonly openModal: OpenModal;
}

const personalDetailsView: React.FunctionComponent<Props> = ({
    classes,
    openModal,
    formDetails,
    updateFormDetails,
    type,
    disableEditButton,
    claimType,
}: Props) => {
    const edit = () => {
        const details = formDetails || {};
        type === 'LifeAssured'
            ? editLifeAssureDetails(openModal, details, 0, updateFormDetails)
            : editPolicyHolderDetails(openModal, details, 0, updateFormDetails);
    };

    const isLifeAssuredSameAsPolicyHolder =
        formDetails?.lifeAssured?.personalDetails?.isTheLifeAssuredTheSameAsPolicyHolder === 'Yes';
    const policyHolderPersonalDetails = formDetails?.policyHolder?.individual?.personalDetails;
    const personalDetails =
        type === 'LifeAssured'
            ? formDetails?.lifeAssured?.personalDetails
            : policyHolderPersonalDetails;
    const isThePolicyHolderAnIndividualOrCompany =
        formDetails?.policyHolder?.policyHolderType ||
        (formDetails?.policyHolder as any)?.IsThePolicyHolderAnIndividualOrCompany ||
        formDetails?.policyHolder?.isThePolicyHolderAnIndividualOrCompany;
    const policyHolderType =
        formDetails?.policyHolder?.policyHolderType || isThePolicyHolderAnIndividualOrCompany;

    const relationshipToPolicyholder =
        personalDetails?.relationshipToPolicyHolder ||
        (personalDetails as any)?.RelationshipToPolicyHolder ||
        personalDetails?.relationshipToPolicyholder;

    const relationshipToPolicyholderLabel = relationshipToPolicyholder
        ? AllRelationships.find(r => r.value === relationshipToPolicyholder)?.label ||
          relationshipToPolicyholder
        : '';

    const otherRelationship =
        personalDetails?.otherRelationship ||
        (personalDetails as any)?.OtherRelationship ||
        (personalDetails as any)?.otherRelationshipToPolicyHolder ||
        (personalDetails as any)?.OtherRelationshipToPolicyHolder ||
        (formDetails as any)?.lifeAssuredOtherRelationship ||
        (formDetails as any)?.LifeAssuredOtherRelationship;

    const lifeAssuredHasMaritalStatusChanged =
        personalDetails?.hasYourMaritalStatusChangedSinceYouTookUpThePolicy ||
        (personalDetails as any)?.HasYourMaritalStatusChangedSinceYouTookUpThePolicy ||
        personalDetails?.hasMaritalStatusChanged;
    const hasMaritalStatusChanged =
        (type === 'LifeAssured' && isLifeAssuredSameAsPolicyHolder
            ? policyHolderPersonalDetails?.hasMaritalStatusChanged
            : personalDetails?.hasMaritalStatusChanged) || '';
    const lifeAssuredMaritalStatusHasChanged =
        lifeAssuredHasMaritalStatusChanged?.toLowerCase() === 'yes';
    const maritalStatusHasChanged = hasMaritalStatusChanged?.toLowerCase() === 'yes';

    const capitalizeFirstLetter = (str?: string | null) =>
        !str ? '' : str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

    let header = 'Personal Details';
    if (type !== 'LifeAssured' && isThePolicyHolderAnIndividualOrCompany === 'Company') {
        header = 'Company Details';
    }
    const companyDetails = formDetails?.policyHolder?.individual?.companyDetails;
    const individualPolicyActivePeriod = personalDetails?.howLongHasPolicyBeenActive;
    const companyPolicyActivePeriod = companyDetails?.howLongHasPolicyBeenActive;

    if (isDreadOrTerminalIllness()) {
        if (type !== 'LifeAssured' && isThePolicyHolderAnIndividualOrCompany === 'Company') {
            return (
                <React.Fragment>
                    <Grid container justify="space-between" className={classes.marginTop2}>
                        <Grid container item xs={10} alignItems="center">
                            <Typography
                                style={{ padding: 10 }}
                                className={classes.heading}
                                variant="body1"
                            >
                                Company Details
                            </Typography>
                        </Grid>
                    </Grid>
                    <Paper className={classes.paper}>
                        <Grid container item xs={12} alignItems="center" justify="flex-end">
                            <Tooltip title="Edit Personal Details">
                                <Button
                                    id="EditPersonalDetails"
                                    color="secondary"
                                    onClick={edit}
                                    disabled={!disableEditButton}
                                >
                                    <Edit />
                                </Button>
                            </Tooltip>
                        </Grid>
                        <Grid container justify="flex-start">
                            <Grid item xs={12} container justify="flex-start">
                                <Grid item xs={12} lg={12}>
                                    <LabeledValue
                                        label="Is the policyholder an individual or company?"
                                        value={isThePolicyHolderAnIndividualOrCompany}
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Company Trading Name"
                                        value={
                                            companyDetails?.companyName ||
                                            companyDetails?.phCompanyName ||
                                            companyDetails?.entityName
                                        }
                                    />
                                </Grid>

                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Company Registration Number"
                                        value={
                                            companyDetails?.companyRegistrationNumber ||
                                            companyDetails?.phCompanyRegistrationNumber ||
                                            companyDetails?.entityRegistrationNumber
                                        }
                                    />
                                </Grid>
                                {companyDetails && (
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Company Registration Date"
                                            value={getDisplayDate(
                                                companyDetails?.companyRegistrationDate ||
                                                    companyDetails?.dateOfIncorporation
                                            )}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Country of incorporation"
                                        value={
                                            companyDetails?.countryOfIncorporation ||
                                            companyDetails?.phCountryOfIncorporation ||
                                            companyDetails?.entityProvince
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                </React.Fragment>
            );
        } else {
            return (
                <React.Fragment>
                    <Grid container justify="space-between" className={classes.marginTop2}>
                        <Grid container item xs={10} alignItems="center">
                            <Typography
                                style={{ padding: 10 }}
                                className={classes.heading}
                                variant="body1"
                            >
                                Personal Details
                            </Typography>
                        </Grid>
                    </Grid>
                    <Paper className={classes.paper}>
                        <Grid container item xs={12} alignItems="center" justify="flex-end">
                            <Tooltip title="Edit Personal Details">
                                <Button
                                    id="EditPersonalDetails"
                                    color="secondary"
                                    onClick={edit}
                                    disabled={!disableEditButton}
                                >
                                    <Edit />
                                </Button>
                            </Tooltip>
                        </Grid>
                        <Grid container justify="flex-start">
                            <Grid item xs={12} container justify="flex-start">
                                <Grid item xs={12} lg={12}>
                                    <LabeledValue
                                        label="Policyholder type"
                                        value={policyHolderType}
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Title"
                                        value={capitalizeFirstLetter(
                                            personalDetails?.title ||
                                                personalDetails?.lifeAssuredTitle
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="First Name"
                                        value={
                                            personalDetails?.firstName ||
                                            personalDetails?.lifeAssuredFirstName
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Surname"
                                        value={
                                            personalDetails?.surname ||
                                            personalDetails?.lifeAssuredSurname
                                        }
                                    />
                                </Grid>
                                {personalDetails?.maiden && (
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Maiden Name"
                                            value={personalDetails?.maiden}
                                        />
                                    </Grid>
                                )}
                            </Grid>

                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Gender"
                                    value={
                                        personalDetails?.gender ||
                                        personalDetails?.lifeAssuredGender
                                    }
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Marital Status"
                                    value={
                                        personalDetails?.maritalStatus ||
                                        personalDetails?.lifeAssuredMaritalStatus
                                    }
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="SA Resident"
                                    value={
                                        personalDetails?.isRSAResident ||
                                        personalDetails?.lifeAssuredIsRSAResident
                                    }
                                />
                            </Grid>
                            {personalDetails?.idNumber && (
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="ID Number"
                                        value={
                                            personalDetails?.idNumber ||
                                            personalDetails?.lifeAssuredIdNumber
                                        }
                                    />
                                </Grid>
                            )}
                            {personalDetails?.passportNumber && (
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Passport Number"
                                        value={
                                            personalDetails?.passportNumber ||
                                            personalDetails?.lifeAssuredPassportNumber
                                        }
                                    />
                                </Grid>
                            )}
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Date of Birth"
                                    value={
                                        getDisplayDate(personalDetails?.dateOfBirth) ||
                                        getDisplayDate(personalDetails?.lifeAssuredDateOfBirth)
                                    }
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Place of Birth"
                                    value={
                                        personalDetails?.placeOfBirth ||
                                        personalDetails?.lifeAssuredPlaceOfBirth
                                    }
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Country of Birth"
                                    value={
                                        personalDetails?.countryOfBirth ||
                                        personalDetails?.lifeAssuredCountryOfBirth
                                    }
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Country of residence"
                                    value={
                                        personalDetails?.countryOfResidence ||
                                        personalDetails?.lifeAssuredCountryOfResidence
                                    }
                                />
                            </Grid>
                            {(personalDetails?.incomeTaxNumber ||
                                personalDetails?.lifeAssuredIncomeTaxNumber) && (
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="SA Income tax number"
                                        value={
                                            personalDetails?.incomeTaxNumber ||
                                            personalDetails?.lifeAssuredIncomeTaxNumber
                                        }
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </React.Fragment>
            );
        }
    } else {
        return (
            <React.Fragment>
                <Grid container justify="space-between" className={classes.marginTop2}>
                    <Grid container item xs={10} alignItems="center">
                        <Typography
                            style={{ padding: 10 }}
                            className={classes.heading}
                            variant="body1"
                        >
                            {header}
                        </Typography>
                    </Grid>
                </Grid>
                <Paper className={classes.paper}>
                    {(type !== 'LifeAssured' ? policyHolderType === 'Individual' : true) && (
                        <Grid container justify="flex-start">
                            <Grid item xs={12} container justify="flex-start">
                                {type !== 'LifeAssured' && (
                                    <Grid item xs={12} container justify="flex-start">
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Is the policyholder an individual or company?"
                                                value={policyHolderType}
                                            />
                                        </Grid>
                                        {claimType === ClaimType.Retrenchment && (
                                            <Grid item xs={4} lg={3}>
                                                <LabeledValue
                                                    label="How long has the policy been active for?"
                                                    value={individualPolicyActivePeriod}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                )}
                                {type === 'LifeAssured' &&
                                    personalDetails?.isTheLifeAssuredTheSameAsPolicyHolder !==
                                        null && (
                                        <Grid item xs={12} lg={12}>
                                            <LabeledValue
                                                label="Is the life assured the same as policyholder?"
                                                value={
                                                    personalDetails?.isTheLifeAssuredTheSameAsPolicyHolder
                                                }
                                            />
                                        </Grid>
                                    )}
                                {type === 'LifeAssured' &&
                                    (personalDetails?.isTheLifeAssuredTheSameAsPolicyHolder ===
                                        'No' ||
                                        isThePolicyHolderAnIndividualOrCompany === 'Company') && (
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Relationship to policyholder?"
                                                value={
                                                    relationshipToPolicyholderLabel ||
                                                    otherRelationship ||
                                                    ''
                                                }
                                            />
                                        </Grid>
                                    )}
                                {type === 'LifeAssured' &&
                                    (personalDetails?.isTheLifeAssuredTheSameAsPolicyHolder ===
                                        'No' ||
                                        isThePolicyHolderAnIndividualOrCompany === 'Company') &&
                                    relationshipToPolicyholder?.toLowerCase() === 'other' && (
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Other relationship"
                                                value={otherRelationship}
                                            />
                                        </Grid>
                                    )}
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Title"
                                        value={capitalizeFirstLetter(
                                            personalDetails?.title ||
                                                personalDetails?.lifeAssuredTitle
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Full first names"
                                        value={
                                            personalDetails?.firstName ||
                                            personalDetails?.lifeAssuredFirstName ||
                                            personalDetails?.fullFirstNames
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Surname"
                                        value={
                                            personalDetails?.surname ||
                                            personalDetails?.lifeAssuredSurname
                                        }
                                    />
                                </Grid>
                                {type === 'LifeAssured' &&
                                    (personalDetails?.isTheLifeAssuredTheSameAsPolicyHolder ===
                                        'No' ||
                                        isThePolicyHolderAnIndividualOrCompany === 'Company') &&
                                    lifeAssuredHasMaritalStatusChanged && (
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Has your marital status changed since you took up the policy?"
                                                value={lifeAssuredHasMaritalStatusChanged}
                                            />
                                        </Grid>
                                    )}
                                {type === 'LifeAssured' &&
                                    (personalDetails?.isTheLifeAssuredTheSameAsPolicyHolder ===
                                        'No' ||
                                        isThePolicyHolderAnIndividualOrCompany === 'Company') &&
                                    lifeAssuredMaritalStatusHasChanged && (
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Marital Status"
                                                value={
                                                    personalDetails?.maritalStatus ||
                                                    (personalDetails as any)?.MaritalStatus ||
                                                    personalDetails?.lifeAssuredMaritalStatus
                                                }
                                            />
                                        </Grid>
                                    )}
                                {type === 'LifeAssured' &&
                                    (personalDetails?.isTheLifeAssuredTheSameAsPolicyHolder ===
                                        'No' ||
                                        isThePolicyHolderAnIndividualOrCompany === 'Company') && (
                                        <>
                                            <Grid item xs={4} lg={3}>
                                                <LabeledValue
                                                    label="Are you a South African resident?"
                                                    value={
                                                        personalDetails?.areYouASouthAfricanResident
                                                    }
                                                />
                                            </Grid>
                                            {personalDetails?.areYouASouthAfricanResident ===
                                                'Yes' && (
                                                <Grid item xs={4} lg={3}>
                                                    <LabeledValue
                                                        label="ID Number"
                                                        value={
                                                            personalDetails?.idNumber ||
                                                            personalDetails?.lifeAssuredIdNumber
                                                        }
                                                    />
                                                </Grid>
                                            )}
                                            {personalDetails?.areYouASouthAfricanResident ===
                                                'No' && (
                                                <>
                                                    <Grid item xs={4} lg={3}>
                                                        <LabeledValue
                                                            label="Passport Number"
                                                            value={
                                                                personalDetails?.passportNumber ||
                                                                personalDetails?.lifeAssuredPassportNumber
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4} lg={3}>
                                                        <LabeledValue
                                                            label="Country of residence"
                                                            value={
                                                                personalDetails?.countryOfResidence ||
                                                                personalDetails?.lifeAssuredCountryOfResidence
                                                            }
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                        </>
                                    )}
                                {(type !== 'LifeAssured' || isLifeAssuredSameAsPolicyHolder) && (
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Cellphone number"
                                            value={
                                                type === 'LifeAssured' &&
                                                isLifeAssuredSameAsPolicyHolder
                                                    ? policyHolderPersonalDetails?.cellphoneNumber
                                                    : personalDetails?.cellphoneNumber
                                            }
                                        />
                                    </Grid>
                                )}
                            </Grid>
                            {(type !== 'LifeAssured' || isLifeAssuredSameAsPolicyHolder) && (
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Email address"
                                        value={
                                            type === 'LifeAssured' &&
                                            isLifeAssuredSameAsPolicyHolder
                                                ? policyHolderPersonalDetails?.emailAddress
                                                : personalDetails?.emailAddress
                                        }
                                    />
                                </Grid>
                            )}
                            {(type !== 'LifeAssured' || isLifeAssuredSameAsPolicyHolder) && (
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Has your marital status changed since you took up the policy?"
                                        value={hasMaritalStatusChanged}
                                    />
                                </Grid>
                            )}
                            {(type !== 'LifeAssured' || isLifeAssuredSameAsPolicyHolder) &&
                                maritalStatusHasChanged && (
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Marital Status"
                                            value={
                                                personalDetails?.maritalStatus ||
                                                (personalDetails as any)?.MaritalStatus ||
                                                personalDetails?.lifeAssuredMaritalStatus
                                            }
                                        />
                                    </Grid>
                                )}
                            {(type !== 'LifeAssured' || isLifeAssuredSameAsPolicyHolder) &&
                                maritalStatusHasChanged &&
                                personalDetails?.maritalStatus === 'Married' && (
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Date of marriage"
                                            value={getDisplayDate(
                                                type === 'LifeAssured' &&
                                                    isLifeAssuredSameAsPolicyHolder
                                                    ? personalDetails?.dateOfMarriage ||
                                                          policyHolderPersonalDetails?.dateOfMarriage
                                                    : personalDetails?.dateOfMarriage
                                            )}
                                        />
                                    </Grid>
                                )}
                            {(type !== 'LifeAssured' || isLifeAssuredSameAsPolicyHolder) &&
                                maritalStatusHasChanged &&
                                personalDetails?.maritalStatus === 'Divorced' && (
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Date of divorce"
                                            value={getDisplayDate(
                                                type === 'LifeAssured' &&
                                                    isLifeAssuredSameAsPolicyHolder
                                                    ? personalDetails?.dateOfDivorce ||
                                                          policyHolderPersonalDetails?.dateOfDivorce
                                                    : personalDetails?.dateOfDivorce
                                            )}
                                        />
                                    </Grid>
                                )}

                            {(type !== 'LifeAssured' || isLifeAssuredSameAsPolicyHolder) && (
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Are you a South African resident?"
                                        value={personalDetails?.areYouASouthAfricanResident}
                                    />
                                </Grid>
                            )}
                            {(type !== 'LifeAssured' || isLifeAssuredSameAsPolicyHolder) &&
                                personalDetails?.areYouASouthAfricanResident === 'Yes' && (
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="ID Number"
                                            value={
                                                personalDetails?.idNumber ||
                                                personalDetails?.lifeAssuredIdNumber
                                            }
                                        />
                                    </Grid>
                                )}
                            {(type !== 'LifeAssured' || isLifeAssuredSameAsPolicyHolder) &&
                                personalDetails?.areYouASouthAfricanResident === 'No' && (
                                    <>
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Passport Number"
                                                value={
                                                    personalDetails?.passportNumber ||
                                                    personalDetails?.lifeAssuredPassportNumber
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Country of residence"
                                                value={
                                                    personalDetails?.countryOfResidence ||
                                                    personalDetails?.lifeAssuredCountryOfResidence
                                                }
                                            />
                                        </Grid>
                                    </>
                                )}
                        </Grid>
                    )}
                    {type !== 'LifeAssured' &&
                        isThePolicyHolderAnIndividualOrCompany === 'Company' && (
                            <Grid container justify="flex-start">
                                <Grid item xs={12} container justify="flex-start">
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Is the policyholder an individual or company?"
                                            value={isThePolicyHolderAnIndividualOrCompany}
                                        />
                                    </Grid>
                                    {claimType === ClaimType.Retrenchment && (
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="How long has the policy been active for?"
                                                value={companyPolicyActivePeriod}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                                <Grid item xs={12} container justify="flex-start">
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Company trading name"
                                            value={
                                                companyDetails?.companyName ||
                                                companyDetails?.phCompanyName
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Company registration number"
                                            value={
                                                companyDetails?.companyRegistrationNumber ||
                                                companyDetails?.phCompanyRegistrationNumber
                                            }
                                        />
                                    </Grid>
                                    {companyDetails && (
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Company registration date"
                                                value={getDisplayDate(
                                                    companyDetails.companyRegistrationDate
                                                )}
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Country of company"
                                            value={
                                                companyDetails?.countryOfIncorporation ||
                                                companyDetails?.phCountryOfIncorporation
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                </Paper>
            </React.Fragment>
        );
    }
    function isDreadOrTerminalIllness() {
        return claimType === ClaimType.Dread || claimType === ClaimType.TerminalIllness;
    }
};

const mapStateToProps = (state: State, ownProps: BaseProps) => ({
    formDetails: ownProps.formDetails ?? getFormDetails(state),
    disableEditButton: ownProps.disableEditButton ?? false,
    updateFormDetails: ownProps.updateFormDetails ?? updateFormDetailsAction,
    claimType: getClaim(state).claimType,
});

const mapDispatchToProps = {
    openModal: openModalAction,
    updateFormDetails: updateFormDetailsAction,
};

export const PersonalDetailsView = connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(personalDetailsView));
